/**
 * Single source of truth for how a `TranslationStatus` is PRESENTED to users.
 *
 * The four GraphQL/Smithy contract values are never renamed here — only the display
 * label and the badge colour live in this module:
 *
 *   COMPLETE    → "Complete"    (green)
 *   IN_PROGRESS → "In progress" (orange)
 *   FAILED      → "Failed"      (red)
 *   CANCELED    → "Canceled"    (brown)
 *
 * Status renders as a coloured `Badge` — a text pill with NO icon — which is what the
 * approved mockup showed. Cloudscape's `StatusIndicator` was the wrong primitive: it
 * always renders an icon and has no prop to suppress it.
 *
 * `Badge`'s semantic `color` prop has no brown, and its only orange is
 * `severity-medium` — the security-finding severity scale, which is deliberately not
 * borrowed: a lifecycle status is not a severity, and those tokens can be retuned
 * independently. Orange and brown are therefore painted by
 * `translationStatus.module.scss` through the badge's `className`. The semantic prop
 * is still passed as the no-stylesheet fallback; the class overrides it with
 * `!important` — see the SCSS for why that is required.
 *
 * Note the orange is a LIGHT fill with NEAR-BLACK text, unlike every other badge
 * here: that is what makes it a bright orange rather than the burnt shade that read
 * as brown next to `Canceled`, and a bright fill cannot carry near-white text at AA.
 *
 * ACCESSIBILITY (WCAG 1.4.1 — Use of Color): with no icon, the VISIBLE TEXT LABEL is
 * the only non-colour carrier of status. Every badge therefore renders its label as
 * its child, and `getTranslationStatusDisplay` always returns a non-empty one. Do not
 * render a bare colour, and do not replace the label with a tooltip or `aria-label`.
 *
 * There is deliberately NO "Draft" entry: an early mockup showed one, but the contract
 * has no `DRAFT` value.
 */
import React from 'react';
import Badge, { BadgeProps } from '@amzn/awsui-components-console/badge';
import { TranslationStatus } from 'src/generated/graphql';
import { TranslateFn, useTranslation } from 'src/i18n/shim/useTranslation';
import styles from './translationStatus.module.scss';

/**
 * The colour vocabulary this module exposes. Deliberately NOT `BadgeProps['color']`:
 * `orange` and `brown` have no semantic counterpart and are realised with a CSS
 * class, so callers and tests reason about the INTENDED colour rather than how it is
 * painted.
 */
export type TranslationStatusColor =
  | 'green'
  | 'orange'
  | 'red'
  | 'brown'
  | 'grey';

export interface TranslationStatusDisplay {
  /**
   * Resource id for the user-visible label, or `undefined` for a status outside the contract, in
   * which case the raw value is shown. It is the non-colour carrier of the status, so never empty.
   */
  readonly labelId?: string;
  /** Intended badge colour. Drives colour only, never an icon. */
  readonly color: TranslationStatusColor;
}

/**
 * How each intended colour is painted: a semantic `Badge` colour (the fallback if the
 * stylesheet is absent) plus, for the two Cloudscape has no token for, the CSS-module
 * class that overrides it.
 */
const BADGE_PRESENTATION: Record<
  TranslationStatusColor,
  { readonly badgeColor: BadgeProps['color']; readonly className?: string }
> = {
  green: { badgeColor: 'green' },
  red: { badgeColor: 'red' },
  grey: { badgeColor: 'grey' },
  // `blue`/`grey` are no-stylesheet fallbacks; the class paints orange and brown.
  orange: { badgeColor: 'blue', className: styles.orange },
  brown: { badgeColor: 'grey', className: styles.brown },
};

/**
 * The complete status → presentation mapping. `Record<TranslationStatus, …>` is
 * exhaustive by construction, so a value added to the GraphQL enum without an entry
 * here — or an entry that is not in the enum — is a compile error.
 */
const TRANSLATION_STATUS_DISPLAY: Record<
  TranslationStatus,
  TranslationStatusDisplay
> = {
  [TranslationStatus.Complete]: {
    labelId: 'translation-status-complete',
    color: 'green',
  },
  [TranslationStatus.InProgress]: {
    labelId: 'translation-status-in-progress',
    color: 'orange',
  },
  [TranslationStatus.Failed]: {
    labelId: 'translation-status-failed',
    color: 'red',
  },
  [TranslationStatus.Canceled]: {
    // Id keeps the British spelling the copy no longer uses: renaming it would orphan the key in
    // the committed packs, which fails `arb-tools compile`.
    labelId: 'translation-status-cancelled',
    color: 'brown',
  },
};

/**
 * Resolves the display metadata for a status.
 *
 * Accepts a plain `string` because some payloads type status loosely (the Smithy
 * `Translation.status` member is a String, not the enum). Unknown values degrade to
 * the raw value with a neutral colour, which keeps the badge from being colour-only.
 */
export function getTranslationStatusDisplay(
  status: string,
): TranslationStatusDisplay {
  return (
    TRANSLATION_STATUS_DISPLAY[status as TranslationStatus] ?? {
      color: 'grey',
    }
  );
}

/** A value outside the contract has no resource and is returned verbatim, so the badge is never
 * colour-only even for a status this build has never heard of. */
export function getTranslationStatusLabel(
  t: TranslateFn,
  status: string,
): string {
  const { labelId } = getTranslationStatusDisplay(status);
  return labelId ? t(labelId) : status;
}

interface TranslationStatusIndicatorProps {
  readonly status: string;
}

/**
 * Renders a status as a coloured Cloudscape `Badge` with its label and no icon.
 *
 * The label child is required for accessibility — see the module doc comment.
 */
export const TranslationStatusIndicator: React.FC<
  TranslationStatusIndicatorProps
> = ({ status }) => {
  const { t } = useTranslation();
  const { color } = getTranslationStatusDisplay(status);
  const { badgeColor, className } = BADGE_PRESENTATION[color];
  return (
    <Badge color={badgeColor} className={className}>
      {getTranslationStatusLabel(t, status)}
    </Badge>
  );
};
